import { makeVega } from "./makeVega";
import { encoding_conversion_aggregate } from "./util";

export async function evalPreProcess(view_data) {
  // produce per-layer enc data
  // context === layers => can have multiple layers
  // for points on line => saves "pathgroup"; ignore "marks"
  // context === facets => can only have a single layer => jam; but needs to be position adjusted given "parent"
  // output: data[layer_index] = { data: [ {d, e} ], encodings: [ {field, channel, datatype, dtype } ], map: {field: { channel, datatype, dtype }, /// } }
  let output = [];
  if (view_data.context === "layers") {
    let layer_keys = Object.keys(view_data.encoding_set);
    layer_keys.forEach((lkey) => {
      let key_split = lkey.split("_");
      key_split[0] = parseInt(key_split[0] - 1)
      key_split[key_split.length - 1] = "pathgroup";
      key_split[key_split.length - 2] = parseInt(key_split[key_split.length - 2] - 1)
      let layer_data = view_data.encoding_set[lkey];
      if (layer_data) {
        let encodings = [];
        let map = {};
        let rep_item = layer_data.items.filter(d => Object.keys(d.e).length === layer_data.encodings.length)[0];
        Object.keys(rep_item.e).forEach(channel => {
          let field = rep_item.e[channel].field;
          let datatype = rep_item.e[channel].datatype;
          let dtype = rep_item.e[channel].datatype_class;
          map[field] = { channel, datatype, dtype }
          encodings.push({ channel, field, datatype, dtype });
        });
        let data = layer_data.items.map(d => {
          d.a = {};
          d.a.x = (d.e.x?.value || 0) + (d.parent.x || 0) + (- view_data.root?.x1 || 0);
          d.a.y = (d.e.y?.value || 0) + (d.parent.y || 0) + (- view_data.root?.y1 || 0);
          return d;
        });
        if (layer_keys.includes(key_split.join("_"))) return;
        output.push({ data, encodings, map });
      }
    })
  } else if (view_data.context === "facets") {
    let layer_keys = Object.keys(view_data.encoding_set);
    let encodings = [];
    let channels = [];
    let map = {};
    let data = [];
    let reps = [];
    layer_keys.forEach((lkey) => {
      let key_split = lkey.split("_");
      key_split[0] = parseInt(key_split[0] - 1)
      key_split[key_split.length - 1] = "pathgroup";
      key_split[key_split.length - 2] = parseInt(key_split[key_split.length - 2] - 1)
      let layer_data = view_data.encoding_set[lkey];
      if (layer_data) {
        let temp_data = layer_data.items.map(d => {
          d.a = {};
          if (d.e.x !== undefined && d.e.y !== undefined) {
            d.a.x = (d.e.x.value || 0) + (d.parent.x || 0) + (- view_data.root?.x1 || 0);
            d.a.y = (d.e.y.value || 0) + (d.parent.y || 0) + (- view_data.root?.y1 || 0);
          } else {
            d.a.x = (d.parent.x || 0) + (parent.width / 2 || 0);
            d.a.y = (d.parent.y || 0) + (parent.height / 2 || 0);
          }
          return d
        });
        if (layer_keys.includes(key_split.join("_"))) return;
        data.push(...temp_data);
        let rep_item = layer_data.items.filter(d => Object.keys(d.e).length === layer_data.encodings.length)[0];
        reps.push(rep_item);
        if (channels.length < layer_data.encodings.length) channels = layer_data.encodings;
      }
    });
    let rep_item = reps.filter(d => Object.keys(d.e).length === channels.length)[0];
    Object.keys(rep_item?.e || {}).forEach(channel => {
      let field = rep_item.e[channel].field;
      let datatype = rep_item.e[channel].datatype;
      let dtype = rep_item.e[channel].datatype_class;
      map[field] = { channel, datatype, dtype }
      encodings.push({ channel, field, datatype, dtype });
    });
    output.push({ data, encodings, map });
  }
  return {
    preprocessed: output,
    context: view_data.context,
    n_layers: view_data.n_layers,
    field_set: view_data.field_set,
    encoding_set: view_data.encoding_set,
    scales_set: view_data.scales_set,
    vega_spec: view_data.vega_spec,
    vl_spec: view_data.vl_spec,
    size: view_data.size,
    chart_size: view_data.chart_size,
    root: view_data.root,
    fronto: view_data.fronto_spec,
    annotation_set: view_data.annotation_set
  };
}

export class LossCompare {
  constructor(source) {
    this.source = source;
  }

  async makeSource() {
    this.source_extractedVG = await makeVega("s", this.source);
    this.source_preprocesed = await evalPreProcess(this.source_extractedVG);
  }

  async compare(targets, pyodide) {
    let targets_preprocessed = []
    let ti = 0;
    for (let target of targets) {
      let target_extractedVG = await makeVega("t" + ti, target);
      let target_preprocesed = await evalPreProcess(target_extractedVG);
      targets_preprocessed.push(target_preprocesed);
      ti += 1;
    }
    let source_preprocesed = this.source_preprocesed;
    let evaluation = await evaluate(source_preprocesed, targets_preprocessed, pyodide);
    return evaluation;
  }
}

let loss_code;

function pythonDoubleQuote(key, value) {
  if (typeof value === 'string') {
    return value.replace(/\"/g, `\\"`);
  }
  return value;
}

export async function evaluate(source, targets, pyodide) {
  // source: source view preprocessed vega data;
  // target: target view preprocessed vega data;
  // output: changes to density; changes to 
  if (!pyodide) console.error('pyodide is not provided')
  if (!loss_code) {
    let loss_code_response = await fetch(location.href + "/loss_compute/loss_compute.py");
    loss_code = await loss_code_response.text();
  }
  let additional_code = `source = json.loads(r"""${JSON.stringify(source, null, 2)}""")
targets = json.loads(r"""${JSON.stringify(targets, null, 2)}""")
loss_outcome = getLoss(source, targets)
json.dumps(loss_outcome)`;
  let outcome = await pyodide.runPythonAsync(loss_code + additional_code);
  // default outcome is map, so needs conversion to json
  let parsedOutcome = { result: JSON.parse(outcome) };
  return parsedOutcome;
}

export const default_cost = {
  identification: {
    weight: 1.5,
    by_channel: {
      position: 3,
      length: 2.5,
      color: 2.2,
      size: 2.2,
      shape: 1.5,
      text: 1.5,
      others: 1
    }
  },
  comparison: {
    weight: 1.5,
    by_channel: {
      position: 3,
      length: 2.5,
      color: 2.2,
      size: 2.2,
      shape: 1.5,
      text: 1.5,
      others: 1
    }
  },
  trend: {
    weight: 5,
    by_channel: {
      "y~x": 3,
      "color~x+y": 2,
      "size~x+y": 2.3,
      "color~x": 1,
      others: 1
    }
  },
  text: {
    weight: 0.5
  },
  density: {
    weight: 6,
    by_channel: {
      occupied: 3,
      overplotted: 5
    }
  }
};

export const lossWeightKeys = {
  identification: "identification",
  comparison: "comparison",
  trend: "trend",
  text: "text",
  density: "density",
  position: "position",
  length: "length",
  color: "color",
  size: "size",
  shape: "shape",
  others: "others",
  Y_X: "y~x",
  COLOR_X_Y: "color~x+y",
  SIZE_X_Y: "size~x+y",
  COLOR_X: "color~x",
  occupied: "occupied",
  overplotted: "overplotted"
}

export const cost_description = {
  identification: {
    weight: { name: "Identification", desc: "Changes to support for identifying data points" },
    by_channel: {
      position: { name: "Position channel", desc: "(e.g., x, y)" },
      length: { name: "Length channel" },
      color: { name: "Color channel", desc: "(e.g., fill, stroke)" },
      size: { name: "Size channel" },
      shape: { name: "Shape channel" },
      text: { name: "Text mark channel" },
      others: { name: "Others" }
    }
  },
  comparison: {
    weight: { name: "Comparison", desc: "Changes to support for comparing pairs of data points" },
    by_channel: {
      position: { name: "Position channel", desc: "(e.g., x, y)" },
      length: { name: "Length channel" },
      color: { name: "Color channel", desc: "(e.g., fill, stroke)" },
      size: { name: "Size channel" },
      shape: { name: "Shape channel" },
      text: { name: "Text mark channel" },
      others: { name: "Others" }
    }
  },
  trend: {
    weight: { name: "Trend", desc: "Changes to support for recognizing multivariate trends" },
    by_channel: {
      "y~x": { name: "Y on X" },
      "color~x+y": { name: "Color on X and Y" },
      "size~x+y": { name: "Size on X and Y" },
      "color~x": { name: "Color on X" },
      others: { name: "Others" }
    }
  },
  text: {
    weight: { name: "Text", desc: "Changes to text elements" }
  },
  density: {
    weight: { name: "Density", desc: "Changes to graphical density in the chart area" },
    by_channel: {
      occupied: { name: "Occupied ratio", desc: "Occupied space ratio by graphical and textual elements in the chart" },
      overplotted: { name: "Overplotted ratio", desc: "Overplotted space ratio by graphical and textual elements in the chart" }
    }
  }
};

export function mergeLoss(lossObject, weights) {
  if (!weights) weights = default_cost;
  let val = 0, tab = {}, aggregate = { density: 0 };
  Object.keys(lossObject).forEach((cat) => {
    if (cat === lossWeightKeys.identification || cat === lossWeightKeys.comparison) {
      let temp = 0, w_sum = 0;
      lossObject[cat].forEach((item) => {
        let channel = encoding_conversion_aggregate[item.source_channel || item.target_channel] || lossWeightKeys.others;
        let weight = (weights[cat].by_channel[channel] || weights[cat].by_channel.others);
        temp += weight * item.loss;
        w_sum += weight
        let wkey = cat + "_" + channel + "_weight", lkey = cat + "_" + channel + "_loss";
        if (tab[wkey] === undefined) tab[wkey] = 0;
        if (tab[lkey] === undefined) tab[lkey] = 0;
        tab[wkey] += weight;
        tab[lkey] += item.loss;
      })
      val += temp * weights[cat].weight / w_sum;
      aggregate[cat] = temp;
      tab[cat + "_0_weight"] = weights[cat].weight;
      tab[cat + "_0_weight_sum"] = w_sum;
    } else if (cat === lossWeightKeys.trend) {
      let temp = 0, w_sum = 0;
      lossObject[cat].forEach((item) => {
        let model = item.source_model || item.target_model;
        let [dep, ind] = model.split("~");
        if (["fill", "stroke", "color"].includes(dep)) dep = "color";
        let model2 = (dep + "~" + ind);
        let weight = (weights[cat].by_channel[model2] || weights[cat].by_channel.others)
        let mean_loss = mean(item.loss_values)
        temp += weight * mean_loss;
        w_sum += weight
        tab[cat + "_" + model2 + "_weight"] = weight;
        tab[cat + "_" + model2 + "_loss"] = mean_loss;
      })
      val += temp * weights[cat].weight;
      aggregate[cat] = temp;
      tab[cat + "_0_weight"] = weights[cat].weight;
      tab[cat + "_0_weight_sum"] = w_sum;
    } else if (cat === lossWeightKeys.text) {
      val += weights[cat].weight * lossObject[cat];
      aggregate[cat] = lossObject[cat];
      tab[cat + "_0_weight"] = weights[cat].weight;
      tab[cat + "_0_loss"] = lossObject[cat];
      tab[cat + "_0_weight_sum"] = weights[cat].weight;
    } else if ([lossWeightKeys.occupied, lossWeightKeys.overplotted].includes(cat)) {
      val += weights.density.by_channel[cat] * Math.abs(lossObject[cat]) * 10 * weights.density.weight / (
        weights.density.by_channel[lossWeightKeys.occupied] + weights.density.by_channel[lossWeightKeys.overplotted]);
      tab["density_" + cat + "_weight"] = weights.density.by_channel[cat];
      tab["density_" + cat + "_loss"] = Math.abs(lossObject[cat]);
      aggregate["density"] += weights.density.by_channel[cat] * Math.abs(lossObject[cat]) * 10 * weights.density.weight / (
        weights.density.by_channel[lossWeightKeys.occupied] + weights.density.by_channel[lossWeightKeys.overplotted]);
    }

  });
  tab["density_0_weight"] = weights.density.weight;
  tab["density_0_weight_sum"] = weights.density.by_channel[lossWeightKeys.occupied] + weights.density.by_channel[lossWeightKeys.overplotted];
  return { val, tab, aggregate };
}

function mean(arr) {
  return arr.reduce((acc, cur) => acc + cur, 0) / arr.length;
}

export async function normalize(raw_tab) {
  let norm_term = {};
  let result = raw_tab.map((row) => {
    let val = 0;
    let tab = {};
    let aggregate = {};
    Object.keys(row).forEach((term) => {
      let cat = term.split("_")[0];
      if (term.endsWith("_loss")) {
        let weight = row[term.replace("_loss", "_weight")];
        let weight_sum = row[cat + "_0_weight_sum"];
        let class_weight = row[cat + "_0_weight"] || 1;
        if (norm_term[term] === undefined) {
          norm_term[term] = get_norm_term(term, raw_tab);
        }
        let _normed = atom_normalize(norm_term[term], row[term]);
        let normed = class_weight * (_normed) / weight_sum / weight;
        val += normed;
        tab[term] = normed;
        if (aggregate[cat] === undefined) aggregate[cat] = 0;
        aggregate[cat] += normed;
      }
    });
    return { val, tab, aggregate };
  });
  let aggregate_tab = result.map((row) => row.aggregate);
  let entire = aggregate_tab.length;
  let ranked = {}, maxes = {};
  Object.keys(result[0]?.aggregate || {}).forEach((cat) => {
    let vals = result.map((row) => row.aggregate[cat]);
    ranked[cat] = vals.sort((a, b) => b - a);
    maxes[cat] = Math.max(...vals);
  })
  result.forEach((row) => {
    row.rank = {};
    row.rank_rel = {};
    row.rel_higher = [];
    Object.keys(row.aggregate).forEach((cat) => {
      row.rank[cat] = get_rank(row.aggregate[cat], cat, aggregate_tab)
      row.rank_rel[cat] = row.aggregate[cat] / maxes[cat];
      // row.rank_rel[cat] = ranked[cat].indexOf(row.aggregate[cat]) / entire;
      if (row.rank_rel[cat] < 0.5) row.rel_higher.push(cat);
    });
    row.strength = make_eval_text(row.rel_higher);
  })
  return result;
}

function get_norm_term(term, tab) {
  let vals = tab.map((d) => d[term]).filter((d) => d !== undefined);
  return { max: Math.max(...vals), min: Math.min(...vals) };
}

function get_rank(val, term, tab) {
  return tab.map((d) => d[term]).sort((a, b) => a - b).indexOf(val) + 1;
}
function atom_normalize(norms, val) {
  if (norms.max - norms.min == 0) return 0;
  return (val - norms.min) / (norms.max - norms.min);
}

export function rerank(recommendations) {
  let loss_values = recommendations.map((d) => d.raw_loss.aggregate);
  let entire = loss_values.length;
  let ranked = {}, maxes = {};
  Object.keys(loss_values[0]).forEach((term) => {
    let vals = loss_values.map((agg) => agg[term]);
    ranked[term] = vals.sort((a, b) => b - a);
    maxes[term] = Math.max(...vals);
  })
  recommendations.forEach((d) => {
    Object.keys(d.norm_loss.rank_rel).forEach((term) => {
      d.norm_loss.rank_rel[term] = d.raw_loss.aggregate[term] / maxes[term];
      // d.norm_loss.rank_rel[term] = ranked[term].indexOf(d.raw_loss.aggregate[term]) / entire;
      if (d.norm_loss.rank_rel[term] < 0.5) d.norm_loss.rel_higher.push(term);
    });
    d.norm_loss.strength = make_eval_text(d.norm_loss.rel_higher);
  });
  return recommendations;
}


export function make_eval_text(rel_higher) {
  if (rel_higher.length > 0) {
    const text_iden = "distinguish data points";
    const text_comp = "compare data points";
    const text_trend = "recognize trend in data";
    const text_text = "find information from text";
    const text_dens = "graphical density in terms of occupation and overplotting"

    let has_density = rel_higher.includes("density");
    let has_message = false;

    let text = "This design has relatively smaller changes to ";

    // 4
    if (rel_higher.includes("identification") &&
      rel_higher.includes("comparison") &&
      rel_higher.includes("trend") &&
      rel_higher.includes("text")) {
      has_message = true;
      text += `ability to ${text_iden}, ${text_comp}, ${text_trend}, ${!has_density ? "and " : ""}${text_text},`
    }
    // 3
    else if (rel_higher.includes("identification") &&
      rel_higher.includes("comparison") &&
      rel_higher.includes("trend")) {
      has_message = true;
      text += `ability to ${text_iden}, ${text_comp}, ${!has_density ? "and " : ""}${text_trend},`
    } else if (rel_higher.includes("identification") &&
      rel_higher.includes("comparison") &&
      rel_higher.includes("text")) {
      has_message = true;
      text += `ability to ${text_iden}, ${text_comp},  ${!has_density ? "and " : ""}${text_text},`
    } else if (rel_higher.includes("identification") &&
      rel_higher.includes("trend") &&
      rel_higher.includes("text")) {
      has_message = true;
      text += `ability to ${text_iden}, ${text_trend}, ${!has_density ? "and " : ""}${text_text},`
    } else if (rel_higher.includes("comparison") &&
      rel_higher.includes("trend") &&
      rel_higher.includes("text")) {
      has_message = true;
      text += `ability to ${text_comp}, ${text_trend}, ${!has_density ? "and " : ""}${text_text},`
    }
    // 2
    else if (rel_higher.includes("identification") &&
      rel_higher.includes("comparison")) {
      has_message = true;
      text += `ability to ${text_iden} and ${text_comp},`
    } else if (rel_higher.includes("identification") &&
      rel_higher.includes("trend")) {
      has_message = true;
      text += `ability to ${text_iden}, and ${text_trend},`
    } else if (rel_higher.includes("comparison") &&
      rel_higher.includes("trend")) {
      has_message = true;
      text += `ability to ${text_comp} and ${text_trend}`
    } else if (rel_higher.includes("identification") &&
      rel_higher.includes("text")) {
      has_message = true;
      text += `ability to ${text_iden} and ${text_text},`
    } else if (rel_higher.includes("comparison") &&
      rel_higher.includes("text")) {
      has_message = true;
      text += `ability to ${text_comp} and ${text_text},`
    } else if (rel_higher.includes("trend") &&
      rel_higher.includes("text")) {
      has_message = true;
      text += `ability to ${text_trend} and ${text_text},`
    }
    // 1
    else if (rel_higher.includes("identification")) {
      has_message = true;
      text += `ability to ${text_iden}`
    } else if (rel_higher.includes("comparison")) {
      has_message = true;
      text += `ability to ${text_comp}`
    } else if (rel_higher.includes("trend")) {
      has_message = true;
      text += `ability to ${text_trend}`
    } else if (rel_higher.includes("text")) {
      has_message = true;
      text += `ability to ${text_text}`
    }

    if (has_density) {
      if (has_message) {
        text += " and ";
      }
      text += `${text_dens}`;
      if (!has_message) {
        text += " while making changes to comparability and overall trend,"
      }
      text += ",";
    }
    text += " compared to the other alternatives."
    return text;
  }
  return "This design may impact message and density more than other designs.";
}

export function resort(recommendations, category) {
  let values = {};
  recommendations.forEach((rec) => {
    if (rec.is_non_rec) {
      rec._new_order = 0;
      rec.index = "0";
      return;
    }
    if (values[rec.level] === undefined) {
      values[rec.level] = {};
    }
    if (values[rec.level][rec.upper || "top"] === undefined) {
      values[rec.level][rec.upper || "top"] = {};
    }
    if (values[rec.level][rec.upper || "top"][category] === undefined) {
      values[rec.level][rec.upper || "top"][category] = recommendations.filter((rec2) => {
        return (rec2.level === rec.level && rec2.upper === rec.upper)
      }).map((rec2) => {
        return category === "original" ? rec2.norm_loss.val : rec2.norm_loss.aggregate[category];
      }).sort((a, b) => a - b);
    }
    let vals = values[rec.level][rec.upper || "top"][category];
    let val = category === "original" ? rec.norm_loss.val : rec.norm_loss.aggregate[category];
    if (rec.level == 1) {
      rec._new_order = (vals.indexOf(val) + 1) * 100;
      rec.index = (vals.indexOf(val) + 1).toString();
    } else if (rec.level == 2) {
      let upper = recommendations.filter((rec2) => rec.upper === rec2.rules.name)[0];
      rec._new_order = upper._new_order + vals.indexOf(val) + 1;
      rec.index = upper.index + "-" + (vals.indexOf(val) + 1).toString();
    }
  });
  recommendations = recommendations.sort((a, b) => a._new_order - b._new_order);
  return recommendations;
}
